import { type ReactNode, useCallback, useMemo, useState } from 'react';

import { I18nContext } from './i18n.context';
import type { Locale } from './locales';
import { DEFAULT_LOCALE, LOCALE_STORAGE_KEY } from './locales';
import { DICTIONARIES, en, type TranslationKey } from './translations';

function loadLocaleFromStorage(): Locale {
    try {
        const saved = localStorage?.getItem(LOCALE_STORAGE_KEY) as Locale | null;
        if (saved === 'cs' || saved === 'en') {
            return saved;
        }
    } catch {
        // storage unavailable
    }
    return DEFAULT_LOCALE;
}

export function I18nProvider({ children }: { children: ReactNode }) {
    const [locale, setLocaleState] = useState<Locale>(loadLocaleFromStorage);

    const setLocale = useCallback((next: Locale) => {
        try {
            localStorage?.setItem(LOCALE_STORAGE_KEY, next);
        } catch {
            // storage unavailable
        }
        setLocaleState(next);
    }, []);

    const t = useCallback((key: TranslationKey) => {
        const dict = DICTIONARIES[locale];
        return dict[key] ?? en[key] ?? key;
    }, [locale]);

    const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);

    return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
